// REMOVE WHEN https://github.com/MetaMask/metamask-mobile/issues/29639 closes.
// (BUG-27 mitigation — UA + provider sniff for MetaMask Mobile in-app
// dApp browser. Detector is consumed by useLogin.tsx for Sentry tag
// enrichment; delete with the rest of BUG-27 when MM ships upstream fix.)

// IMPORTANT — Window.ethereum typing.
// We do NOT add a `declare global { interface Window { ethereum?: ... } }`
// block here. viem already ships `Window.ethereum?: EIP1193Provider | undefined`
// project-wide via `viem/window` (loaded transitively through wagmi —
// confirmed import at src/configs/wagmi-minimal.ts:10 — and ambient across
// the whole compilation unit). Adding a 4th interface-merge declaration with
// a different property type (`unknown`) collides with viem / @reown's existing
// declarations and triggers TS2403 ("Subsequent property declarations must
// have the same type"). Build would fail tsc-strict. See blueprint-review.md
// P2-01.
//
// Instead the SSR wrapper passes `window.ethereum` straight to the pure
// inner function. viem types `window.ethereum` as `EIP1193Provider | undefined`
// for free; we widen to `unknown` at the function boundary so the inner
// detector can structurally narrow without depending on viem's specific shape.

/**
 * Pure detector. Takes the UA string + the suspected `window.ethereum`
 * object (typed `unknown` because EIP1193Provider does NOT expose
 * `isMetaMask` at the type level — it's a wallet-specific extension that
 * lives on the runtime object via index signature; we narrow with `in` +
 * `typeof` + strict `=== true`).
 *
 * Detection rule:
 *  - userAgent must include the literal substring "MetaMaskMobile" (the
 *    token MetaMask Mobile appends to its WebView UA since v6.x).
 *  - ethereum must be a non-null object that has an `isMetaMask` property
 *    of type `boolean` whose value is strictly === true.
 */
export function detectMmMobileFromUserAgent(
  userAgent: string,
  ethereum: unknown,
): boolean {
  if (typeof userAgent !== "string") return false;
  if (!userAgent.includes("MetaMaskMobile")) return false;
  if (typeof ethereum !== "object" || ethereum === null) return false;
  if (!("isMetaMask" in ethereum)) return false;
  // After `in` narrowing, ethereum is `object & { isMetaMask: unknown }`.
  // Defensive: confirm boolean type before strict-equality compare.
  if (typeof ethereum.isMetaMask !== "boolean") return false;
  return ethereum.isMetaMask === true;
}

/**
 * SSR-safe wrapper. On the server (no `window`/`navigator`) returns false.
 * On the client reads `navigator.userAgent` + `window.ethereum`.
 *
 * `window.ethereum` is typed `EIP1193Provider | undefined` here by viem's
 * ambient declaration; we pass it as `unknown` to the pure inner function
 * which performs runtime narrowing (callers don't need viem's specific
 * shape).
 */
export function isMetaMaskMobileInAppBrowser(): boolean {
  if (typeof window === "undefined") return false;
  if (typeof navigator === "undefined") return false;
  return detectMmMobileFromUserAgent(navigator.userAgent, window.ethereum);
}
